import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Menu, MenuItem } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { fetchFamiliesByHandles, fetchPeopleByHandles, fetchPersonByGrampsId, personExtended, restPersonIsMale, restPersonName, type RestPerson } from '../api/grampsRest';

function unique<T>(values: Array<T | undefined>): T[] {
    return [...new Set(values.filter((value): value is T => value !== undefined))];
}

interface PersonRelatives {
    children: RestPerson[];
    spouses: RestPerson[];
}

async function fetchPersonRelatives(personGrampsId: string, token: string | undefined): Promise<PersonRelatives | undefined> {
    const person = await fetchPersonByGrampsId(personGrampsId, token);
    if (!person?.handle) return undefined;
    const extension = personExtended(person);
    const familyHandles = unique(person.family_list ?? []);
    const families = (extension.families?.length ?? 0) > 0 || familyHandles.length === 0
        ? extension.families ?? []
        : await fetchFamiliesByHandles(familyHandles, token);
    const childHandles = unique(families.flatMap((family) => (family.child_ref_list ?? []).map((child) => child.ref)));
    const spouseHandles = unique(families.flatMap((family) => [family.father_handle, family.mother_handle]))
        .filter((handle) => handle !== person.handle);
    const relatives = await fetchPeopleByHandles(unique([...childHandles, ...spouseHandles]), token);
    const relativesByHandle = new Map(relatives.filter((relative) => relative.handle).map((relative) => [relative.handle!, relative]));
    const children = childHandles.flatMap((handle) => { const child = relativesByHandle.get(handle); return child ? [child] : []; });
    const spouses = spouseHandles.flatMap((handle) => { const spouse = relativesByHandle.get(handle); return spouse ? [spouse] : []; });
    return { children, spouses };
}

function relativeLabel(person: RestPerson, relation: 'child' | 'spouse'): string {
    const isMale = restPersonIsMale(person);
    const kind = relation === 'child' ? (isMale ? 'son fils' : 'sa fille') : (isMale ? 'son époux' : 'son épouse');
    return `${kind} ${restPersonName(person)}`;
}

function CenterSubmenu({ label, personGrampsId, relativesQuery, onSelect, anchorEl, onOpen, onClose }: {
    label: string;
    personGrampsId: string;
    relativesQuery: UseQueryResult<PersonRelatives | undefined>;
    onSelect: (personId: string) => void;
    anchorEl: HTMLElement | null;
    onOpen: (target: HTMLElement) => void;
    onClose: () => void;
}) {
    const relatives = relativesQuery.data;
    return <>
        {/* onMouseEnter opens it Windows-context-menu style, as soon as the pointer
            reaches the row; onClick keeps it reachable from touch/keyboard. */}
        <MenuItem onMouseEnter={(event) => onOpen(event.currentTarget)} onClick={(event) => onOpen(event.currentTarget)} sx={{ justifyContent: 'space-between', gap: 2 }}>
            {label}<ChevronRightIcon fontSize="small" />
        </MenuItem>
        <Menu
            anchorEl={anchorEl}
            open={anchorEl !== null}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <MenuItem onClick={() => { onClose(); onSelect(personGrampsId); }}>cette personne</MenuItem>
            {relativesQuery.isLoading && <MenuItem disabled>Chargement…</MenuItem>}
            {relatives?.children.filter((child) => child.gramps_id).map((child) => (
                <MenuItem key={`child-${child.handle}`} onClick={() => { onClose(); onSelect(child.gramps_id!); }}>{relativeLabel(child, 'child')}</MenuItem>
            ))}
            {relatives?.spouses.filter((spouse) => spouse.gramps_id).map((spouse) => (
                <MenuItem key={`spouse-${spouse.handle}`} onClick={() => { onClose(); onSelect(spouse.gramps_id!); }}>{relativeLabel(spouse, 'spouse')}</MenuItem>
            ))}
        </Menu>
    </>;
}

/**
 * Right-click context menu for a person shown on the ancestry map or the radial
 * ascendency chart. Mount only while a person was right-clicked (the parent owns
 * the open/close state); each mount starts with fresh submenu state, so there is
 * no stale-submenu carry-over between different right-clicks.
 */
export function PersonContextMenu({ anchorPosition, personGrampsId, treeName, token, onClose, onCenter, onGoTo }: {
    anchorPosition: { mouseX: number; mouseY: number };
    personGrampsId: string;
    treeName: string | null;
    token: string | undefined;
    onClose: () => void;
    onCenter: (personId: string) => void;
    onGoTo: (personId: string) => void;
}) {
    const relativesQuery = useQuery({
        queryKey: ['person-relatives', treeName, personGrampsId],
        queryFn: () => fetchPersonRelatives(personGrampsId, token),
    });
    // Only one submenu open at a time: hovering the other top-level item should
    // switch straight to it, like a native (e.g. Windows) context menu.
    const [openSubmenu, setOpenSubmenu] = useState<{ which: 'center' | 'goto'; anchorEl: HTMLElement } | null>(null);
    return <Menu
        open
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: anchorPosition.mouseY, left: anchorPosition.mouseX }}
    >
        <CenterSubmenu
            label="Centrer sur"
            personGrampsId={personGrampsId}
            relativesQuery={relativesQuery}
            onSelect={(personId) => { onCenter(personId); onClose(); }}
            anchorEl={openSubmenu?.which === 'center' ? openSubmenu.anchorEl : null}
            onOpen={(target) => setOpenSubmenu({ which: 'center', anchorEl: target })}
            onClose={() => setOpenSubmenu((current) => (current?.which === 'center' ? null : current))}
        />
        <CenterSubmenu
            label="Aller à"
            personGrampsId={personGrampsId}
            relativesQuery={relativesQuery}
            onSelect={(personId) => { onGoTo(personId); onClose(); }}
            anchorEl={openSubmenu?.which === 'goto' ? openSubmenu.anchorEl : null}
            onOpen={(target) => setOpenSubmenu({ which: 'goto', anchorEl: target })}
            onClose={() => setOpenSubmenu((current) => (current?.which === 'goto' ? null : current))}
        />
    </Menu>;
}
